/**
 * Activity service — the wired app-wide ActivityTracker singleton plus
 * the read endpoints for the breadcrumb modal's User Activity tab.
 */
import api from './api';
import { msalInstance } from './authConfig';
import { ActivityTracker, ActivityEvent } from './activityTracker';

export interface UserActivityRow {
  id: number;
  user_email: string;
  event_type: string;
  occurred_at: string;
  page_path: string;
  target: string | null;
  detail: string | null;
  duration_ms: number | null;
  session_id: string | null;
}

export interface ActivityResponse {
  /** Days the server bounded the window to — NOT how long rows are retained.
   *  Titled from this so the tab cannot drift from the query behind it. */
  days: number;
  items: UserActivityRow[];
}

const defaultSend = async (events: ActivityEvent[]): Promise<void> => {
  await api.post('/activity/batch', { events });
};

/** App-wide singleton. Enabled only when an MSAL account exists. */
export const activityTracker = new ActivityTracker({
  send: defaultSend,
  isEnabled: () => {
    try {
      return msalInstance.getAllAccounts().length > 0;
    } catch {
      return false;
    }
  },
});

/** Older deploys answered without `days`; fall back to the window they applied. */
const LEGACY_MODAL_DAYS = 7;

const withDays = (data: ActivityResponse): ActivityResponse => ({
  days: data.days ?? LEGACY_MODAL_DAYS,
  items: data.items ?? [],
});

const activityService = {
  getMyActivity: async (signal?: AbortSignal): Promise<ActivityResponse> => {
    const res = await api.get<ActivityResponse>('/activity/me', { signal });
    return withDays(res.data);
  },
  /** Admin (Developer group) — any user's activity by Azure AD oid. */
  getUserActivity: async (userId: string, signal?: AbortSignal): Promise<ActivityResponse> => {
    const res = await api.get<ActivityResponse>(`/activity/user/${userId}`, { signal });
    return withDays(res.data);
  },
};

export default activityService;
